import Icon from '@components/utils/Icon'
import cn from '@components/utils/cn'
import { Avatar } from '@components/atoms/Avatar'
import { IconHint } from '@components/molecules/IconHint'

import { scopedTranslation } from '@utils/I18n'

import useUnassignShiftSlotMutation from '../unassign_shifts/useUnassignShiftSlotMutation'

import type { ShiftSlotWithShift } from '../shifts/types'

const t = scopedTranslation('roster_board')

export type ChipVariant = 'published' | 'added' | 'neutral'

const CHIP_VARIANT: Record<ChipVariant, { chip: string; remove: string }> = {
  published: { chip: 'tw:border-success-200 tw:bg-success-50 tw:text-success-800', remove: 'tw:text-success-600' },
  added: { chip: 'tw:border-warning-300 tw:bg-warning-50 tw:text-warning-700', remove: 'tw:text-warning-600' },
  neutral: { chip: 'tw:border-gray-200 tw:bg-gray-50 tw:text-gray-700', remove: 'tw:text-gray-400' },
}

type AssignedSlotChipProps = {
  slot: ShiftSlotWithShift
  onChange: () => void
  variant?: ChipVariant
}

export function AssignedSlotChip({ slot, onChange, variant = 'published' }: AssignedSlotChipProps) {
  const { unassignShiftSlot, isPending } = useUnassignShiftSlotMutation()

  const { supporter } = slot
  if (!supporter) return null

  const fullName = supporter.fullName ?? ''

  const handleUnassign = () => {
    unassignShiftSlot({
      id: slot.id,
      onSuccess: onChange,
    })
  }

  return (
    <span
      className={cn(
        'tw:inline-flex tw:items-center tw:gap-0.5 tw:rounded-full tw:border tw:py-0.5 tw:pl-0.5 tw:pr-0.5 tw:text-xs tw:font-medium',
        CHIP_VARIANT[variant].chip
      )}
    >
      {/* TODO(#6852): render static until roster-scoped profile access lands; the group-scoped profile route can 404 when the roster group is not the supporter's. */}
      <span className="tw:inline-flex tw:items-center tw:gap-1.5 tw:pl-0.5 tw:pr-1">
        <Avatar name={fullName} />
        <span>{fullName}</span>
      </span>
      <IconHint label={t('remove_supporter', { name: fullName })}>
        <button
          type="button"
          onClick={handleUnassign}
          disabled={isPending}
          aria-label={t('remove_supporter', { name: fullName })}
          className={cn(
            // >=24px hit target (WCAG 2.5.8) around a ~13px glyph.
            'tw:flex tw:h-6 tw:w-6 tw:shrink-0 tw:cursor-pointer tw:items-center tw:justify-center tw:rounded-full tw:hover:bg-error-50 tw:hover:text-error-600 tw:focus:outline-hidden tw:focus-visible:ring-2 tw:focus-visible:ring-error-400',
            CHIP_VARIANT[variant].remove
          )}
        >
          <Icon type="x-close" className="tw:h-3.5 tw:w-3.5" aria-hidden />
        </button>
      </IconHint>
    </span>
  )
}
